import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { formatTimeSaudi } from '../../utils/saudiTime';

export default function OrderReceiverOrdersHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError('');
      try {
        const res = await apiService.orders.getSalesOrdersHistory();
        if (!cancelled && Array.isArray(res.data)) setOrders(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load orders history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const formatDateTime = formatTimeSaudi;

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2 sm:mb-4">Orders History</h1>
        <p className="text-gray-600 text-sm mb-4 sm:mb-6">All Bike Sales orders.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {loading && <div className="py-8 text-center text-gray-500">Loading…</div>}
        {!loading && orders.length === 0 && (
          <div className="py-8 text-center text-gray-500 border border-gray-200 rounded-lg">
            No orders yet.
          </div>
        )}
        {!loading && orders.length > 0 && (
          <div className="space-y-4">
            {orders.map((order) => {
              const itemCount = (order.items || []).reduce((sum, i) => sum + (i.orderQty || 0), 0);
              return (
                <div
                  key={order._id}
                  className="border border-gray-200 rounded-lg p-3 sm:p-4 border-l-4 border-l-blue-400"
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-800 text-base sm:text-lg">{order.orderNo}</div>
                        {order.customerName && (
                          <div className="text-sm sm:text-base text-gray-600 mt-0.5">Customer: {order.customerName}</div>
                        )}
                        {order.customerPhone && (
                          <div className="text-sm sm:text-base text-gray-600 mt-0.5">Phone: {order.customerPhone}</div>
                        )}
                        {order.deliverySlot && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <span className="text-base sm:text-lg font-bold text-gray-800">Delivery Slot: </span>
                            <span className="text-base sm:text-lg font-bold text-blue-700">{order.deliverySlot}</span>
                          </div>
                        )}
                        <div className="text-sm sm:text-base text-gray-500 mt-1">
                          {formatDateTime(order.dateTime)} · {itemCount} item(s)
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="inline-block px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                          {order.status || '—'}
                        </span>
                        {order.isReady && (
                          <span className="inline-block px-3 py-1.5 rounded-full text-sm font-bold bg-green-100 text-green-800 border border-green-200">
                            ✓ Ready
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {order.items && order.items.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                        {order.items.map((item, idx) => {
                          const qty = Number(item.orderQty) || 0;
                          const unitPrice = Number(item.unitPrice) || 0;
                          const lineTotal = qty * unitPrice;
                          return (
                            <li key={idx} className="flex justify-between gap-3">
                              <span>{item.itemName} × {qty}</span>
                              <span className="font-medium tabular-nums whitespace-nowrap">
                                {unitPrice > 0 ? `SAR ${lineTotal.toFixed(2)}` : '—'}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      {order.totalAmount != null && Number(order.totalAmount) > 0 && (
                        <div className="flex justify-between text-base font-semibold text-gray-800 mt-3 pt-2 border-t border-gray-100">
                          <span>Total</span>
                          <span className="tabular-nums">SAR {Number(order.totalAmount).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

